import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Eye, Search, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { FormFilters, FormFilterState } from "@/components/forms/FormFilters";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTenant } from "@/contexts/TenantContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FormSubmissionsViewer } from "@/components/forms/FormSubmissionsViewer";
import { DynamicForm } from "@/components/forms/DynamicForm";
import { logCrud } from "@/services/activities";

type Form = {
  id: string;
  nome: string;
  descricao: string | null;
  slug: string;
  status: "ativo" | "inativo" | "rascunho";
  permite_multiplas_respostas: boolean;
  exige_autenticacao: boolean;
  data_inicio: string | null;
  data_fim: string | null;
  created_at: string;
  updated_at: string;
};

export default function Forms() {
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewingSubmissions, setViewingSubmissions] = useState<string | null>(null);
  const [previewingForm, setPreviewingForm] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [filters, setFilters] = useState<FormFilterState>({
    searchTerm: "",
    status: [],
  });

  const { data: forms, isLoading } = useQuery({
    queryKey: ["forms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forms")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as Form[];
    },
  });


  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("forms").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      toast.success("Formulário excluído com sucesso");
      if (deleteId) {
        void logCrud({ entitySlug: "entity:forms", operationSlug: "delete", entityId: deleteId, screenSlug: "configuracoes/formularios" });
      }
      setDeleteId(null);
    },
    onError: () => {
      toast.error("Erro ao excluir formulário");
    },
  });

  const handleEdit = (form: Form) => {
    navigate(`/configuracoes/formularios/${form.id}`);
  };

  const handleNew = () => {
    navigate("/configuracoes/formularios/novo");
  };

  const matchesFilters = (form: Form) => {
    // Filtro por busca
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        form.nome?.toLowerCase().includes(searchLower) ||
        form.descricao?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtro por status
    if (filters.status.length > 0) {
      if (!filters.status.includes(form.status)) return false;
    }

    return true;
  };

  const filteredForms = useMemo(() => {
    if (!forms) return [] as Form[];
    return forms.filter((form) => matchesFilters(form));
  }, [forms, filters]);

  const paginatedForms = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredForms.slice(start, start + itemsPerPage);
  }, [filteredForms, currentPage, itemsPerPage]);

  // Calcular contagens para os filtros
  const facetCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
    };

    if (!forms) return counts;

    forms.forEach((form) => {
      const matchesOtherFilters = (exceptFilter?: "status") => {
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            form.nome?.toLowerCase().includes(searchLower) ||
            form.descricao?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        if (exceptFilter !== "status" && filters.status.length > 0) {
          if (!filters.status.includes(form.status)) return false;
        }

        return true;
      };

      if (form.status && matchesOtherFilters("status")) {
        counts.status[form.status] = (counts.status[form.status] || 0) + 1;
      }
    });

    return counts;
  }, [forms, filters]);

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    forms?.forEach((f) => {
      if (f.status) statuses.add(f.status);
    });
    return Array.from(statuses).sort();
  }, [forms]);

  const handleFiltersChange = (newFilters: FormFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, searchTerm: "" });
        },
      });
    }
    
    filters.status.forEach((s) => {
      chips.push({
        id: `status:${s}`,
        label: `Status: ${s}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, status: filters.status.filter((x) => x !== s) });
        },
      });
    });
    
    return chips;
  }, [filters]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      ativo: "default",
      inativo: "secondary",
      rascunho: "outline",
    };
    return (
      <Badge variant={variants[status] || "outline"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Formulários</h1>
          <p className="text-muted-foreground">
            Gerencie formulários customizados para produtos, clientes e mais
          </p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Formulário
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle>Lista de Formulários</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                  />
                </div>
                <FormFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  statuses={allStatuses}
                  counts={facetCounts}
                />
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : filteredForms && filteredForms.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedForms.map((form) => (
                    <TableRow key={form.id}>
                      <TableCell className="font-medium">{form.nome}</TableCell>
                      <TableCell>{getStatusBadge(form.status)}</TableCell>
                      <TableCell>
                        {new Date(form.created_at).toLocaleDateString("pt-BR")}
                      </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewingForm(form.id)}
                          title="Visualizar formulário"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewingSubmissions(form.id)}
                          title="Ver respostas"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(form)}
                          title="Editar formulário"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteId(form.id)}
                          title="Excluir formulário"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredForms.length / itemsPerPage)}
                totalItems={filteredForms.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </>
          ) : forms && forms.length > 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum formulário encontrado com os filtros aplicados.
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum formulário cadastrado. Crie seu primeiro formulário!
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={!!previewingForm} 
        onOpenChange={(open) => {
          // Só fechar quando explicitamente fechado (não ao clicar dentro)
          if (!open) {
            setPreviewingForm(null);
          }
        }}
      >
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // Prevenir fechamento ao clicar fora (opcional - pode remover se quiser permitir)
            // e.preventDefault();
          }}
        >
          <DialogHeader className="relative">
            <DialogTitle>Visualização do Formulário</DialogTitle>
            <DialogDescription>
              Esta é uma prévia de como o formulário aparecerá para quem for preenchê-lo.
            </DialogDescription>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setPreviewingForm(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          {previewingForm && (
            <div 
              className="mt-4"
              onClick={(e) => {
                // Prevenir propagação de eventos de clique dentro do formulário
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                // Prevenir propagação de eventos de mouse
                e.stopPropagation();
              }}
            >
              <DynamicForm
                formId={previewingForm}
                readOnly={true}
                onSubmit={() => {
                  // Não faz nada em modo preview
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingSubmissions} onOpenChange={() => setViewingSubmissions(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="relative">
            <DialogTitle>Respostas do Formulário</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setViewingSubmissions(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          {viewingSubmissions && (
            <FormSubmissionsViewer formId={viewingSubmissions} />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Formulário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este formulário? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) {
                  deleteMutation.mutate(deleteId);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


